import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../services/api';
import { formatChinaDate } from '../utils/date';

type Position = {
  _id: string;
  title: string;
  level: 'Master' | 'PhD' | 'Postdoc' | 'Research Assistant' | 'Other';
  direction?: string;
  status: 'open' | 'closed';
  description?: string;
  deadline?: string;
};

const isHtml = (s: string) => /<[a-z][\s\S]*>/i.test(s);

const RecruitmentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [position, setPosition] = useState<Position | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.get<Position>(`/recruitment-positions/${id}`);
        setPosition(res.data);
        setError(null);
      } catch (err) {
        console.error(err);
        setError('Position not found or closed.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  return (
    <section className="bg-gradient-to-b from-white via-white to-brand-redSoft/40">
      <div className="mx-auto max-w-3xl px-4 py-10 md:py-14">
        <div className="mb-4 text-xs text-neutral-500">
          <Link to="/recruitment" className="hover:text-brand-red">
            Recruitment
          </Link>
          <span className="mx-1.5 text-neutral-400">/</span>
          <span>Details</span>
        </div>

        {loading ? (
          <p className="text-sm text-neutral-600">Loading...</p>
        ) : error ? (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : !position ? (
          <p className="text-sm text-neutral-600">Position not found.</p>
        ) : (
          <>
            <header className="mb-6">
              <h1 className="font-serif text-2xl font-semibold tracking-tight text-neutral-900 md:text-3xl">
                {position.title}
              </h1>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs text-neutral-700">
                  {position.level}
                </span>
                {position.deadline && (
                  <span className="text-xs text-neutral-500">
                    Deadline: {formatChinaDate(position.deadline, 'en-GB')}
                  </span>
                )}
              </div>
              {position.direction && (
                <p className="mt-2 text-sm text-neutral-600">
                  Research Direction: {position.direction}
                </p>
              )}
            </header>

            {position.description &&
              (isHtml(position.description) ? (
                <div
                  className="prose prose-sm max-w-none text-neutral-700 prose-p:my-2 prose-ul:my-2 prose-li:my-0"
                  dangerouslySetInnerHTML={{ __html: position.description }}
                />
              ) : (
                <p className="whitespace-pre-wrap text-sm text-neutral-700">
                  {position.description}
                </p>
              ))}

            <div className="mt-8">
              <Link
                to="/recruitment"
                className="text-xs text-neutral-500 hover:text-brand-red"
              >
                ← Back to list
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default RecruitmentDetail;
